import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, KeyRound, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { useActivationComplete, useActivationStatus } from "@/lib/hooks/auth";

/**
 * /activate/$token — pagina di completamento attivazione utente (Phase 10G).
 *
 * Flow:
 * 1. Pre-form check: GET /auth/activation/{token} -> renderizza UI in base
 *    alla validity. Token scaduto/consumato -> invitiamo a richiedere
 *    contatto admin (no "request new token" endpoint anonimo per security).
 * 2. Form password: minimo 12 chars (mirror BE PasswordPolicy 6 rule).
 *    On submit -> POST /auth/activation/{token}.
 * 3. Success -> redirect a /login con conferma.
 *
 * Public route: nessun auth guard. Il token e' l'autenticazione.
 */

const FormSchema = z
  .object({
    new_password: z
      .string()
      .min(12, "validation.password_min")
      .max(128, "validation.password_max"),
    confirm_password: z.string(),
  })
  .refine((v) => v.new_password === v.confirm_password, {
    message: "validation.password_mismatch",
    path: ["confirm_password"],
  });

type FormValues = z.infer<typeof FormSchema>;

export const Route = createFileRoute("/activate/$token")({
  component: ActivatePage,
});

function ActivatePage(): JSX.Element {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const statusQ = useActivationStatus(token);
  const complete = useActivationComplete(token);
  const [done, setDone] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { new_password: "", confirm_password: "" },
    mode: "onBlur",
  });

  async function onSubmit(values: FormValues): Promise<void> {
    try {
      await complete.mutateAsync({
        new_password: values.new_password,
        enroll_mfa: true,
      });
      setDone(true);
    } catch {
      // surfaced via complete.error block
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="size-5 text-primary" aria-hidden="true" />
            {t("activate.title")}
          </CardTitle>
          <CardDescription>{t("activate.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status check loading */}
          {statusQ.isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              {t("common.loading")}
            </div>
          )}

          {/* Token invalid / expired */}
          {!statusQ.isLoading && statusQ.data && !statusQ.data.valid && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <div className="flex items-center gap-2 font-medium">
                <AlertCircle className="size-4" aria-hidden="true" />
                {t("activate.invalid_title")}
              </div>
              <p className="mt-1 text-xs">{t("activate.invalid_hint")}</p>
              <Link to="/login" className="mt-2 inline-block text-xs text-primary underline">
                {t("activate.back_to_login")}
              </Link>
            </div>
          )}

          {/* Status query error (network / 5xx) */}
          {!statusQ.isLoading && statusQ.error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <div className="font-medium">{t("common.error_title")}</div>
              <ul className="ml-4 mt-1 list-disc space-y-0.5 text-xs">
                {formatApiError(statusQ.error).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Success */}
          {done && (
            <div className="rounded-md border border-success/40 bg-success/5 p-3 text-sm text-success">
              <div className="flex items-center gap-2 font-medium">
                <CheckCircle2 className="size-4" aria-hidden="true" />
                {t("activate.success_title")}
              </div>
              <p className="mt-1 text-xs">{t("activate.success_hint")}</p>
              <Link to="/login" className="mt-2 inline-block text-xs text-primary underline">
                {t("activate.go_to_login")}
              </Link>
            </div>
          )}

          {/* Form (only when token valid + not done) */}
          {!done && statusQ.data?.valid && (
            <form
              onSubmit={(e) => {
                void form.handleSubmit(onSubmit)(e);
              }}
              className="space-y-3"
            >
              <div className="space-y-1">
                <Label htmlFor="new_password" className="text-xs font-medium">
                  {t("activate.new_password")}
                </Label>
                <Input
                  id="new_password"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  disabled={complete.isPending}
                  {...form.register("new_password")}
                />
                <p className="text-xs text-muted-foreground">
                  {t("activate.password_hint")}
                </p>
                {form.formState.errors.new_password?.message && (
                  <p className="text-xs text-destructive" role="alert">
                    {t(String(form.formState.errors.new_password.message))}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="confirm_password" className="text-xs font-medium">
                  {t("activate.confirm_password")}
                </Label>
                <Input
                  id="confirm_password"
                  type="password"
                  autoComplete="new-password"
                  disabled={complete.isPending}
                  {...form.register("confirm_password")}
                />
                {form.formState.errors.confirm_password?.message && (
                  <p className="text-xs text-destructive" role="alert">
                    {t(String(form.formState.errors.confirm_password.message))}
                  </p>
                )}
              </div>

              {complete.isError && (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
                  <div className="font-medium">{t("common.error_title")}</div>
                  <ul className="ml-4 mt-1 list-disc space-y-0.5">
                    {formatApiError(complete.error).map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={complete.isPending}>
                {complete.isPending && (
                  <Loader2 className="mr-2 size-4 animate-spin" aria-hidden="true" />
                )}
                {t("activate.submit")}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
